import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Box,
    Button,
    Flex,
    Text,
    HStack,
    VStack,
    Spinner,
    Center,
    useToast,
    Tag
} from '@chakra-ui/react';
import {
    FiArrowLeft,
    FiCalendar,
    FiClock,
    FiUsers,
    FiCheckCircle,
    FiMessageSquare,
    FiFileText,
    FiAlertCircle
} from 'react-icons/fi';
import { getApiHeaders, formatNumber, hasPermission } from '../../../../utils/DefaultFunction';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import BoxWrapper from '../../../../components/BoxWrapper/BoxWrapper';
import Header from '../../../../components/Header';
import CSVCampaignReportWithTable from './CSVDeliveryLogs';
import { fetchWrapper } from '../../../../helpers';
import { useCsvExport } from '../../../../context/CsvExportContext';
import moment from 'moment';
import API from '../../../../helpers/axiosInstance';
import { getChannel, getChannelLabel } from '../../../../utils/channels';


export default function CSVCampaignReport() {
    const [campaignData, setCampaignData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(100);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);

    // Filter state
    const [filters, setFilters] = useState({});

    // ── Table child states ───────────────────────────────────────────────────
    const [channelOptions, setChannelOptions] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [statusList, setStatusList] = useState([]);
    const [isDownloading, setIsDownloading] = useState(false);
    const { startExport, isExporting } = useCsvExport();
    const [autoRefreshInterval, setAutoRefreshInterval] = useState(0);

    const customer_id = JSON.parse(localStorage.getItem("customer"))?.customer_id;

    const toast = useToast();
    const { id } = useParams();
    const csvId = id;
    const navigate = useNavigate();

    const location = useLocation();
    const [featurePermissions, setFeaturePermissions] = useState([]);
    const [featurePermissionsLoading, setFeaturePermissionsLoading] = useState(true);
    // Backend groups every CSV-sender permission (list/create/edit/delete/view/report)
    // under the list page's own feature slug, not this page's own route (which
    // includes the campaign id) — querying with the literal path returns nothing.
    const permParams = "tools/csv-message-sender";

    const fetchPermissions = async () => {
        setFeaturePermissionsLoading(true);
        try {
            const url = `${process.env.REACT_APP_API_URL}/permission/feature/list?slug=${permParams}`;
            const response = await API.get(url, { headers: getApiHeaders() });
            setFeaturePermissions(response.data.data || []);
        } catch (error) {
            console.error("Error fetching permissions:", error);
            setFeaturePermissions([]);
        } finally {
            setFeaturePermissionsLoading(false);
        }
    };
    useEffect(() => {
        fetchPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const csvMessageSenderReportPermission = hasPermission("csv-message-sender-report", featurePermissions);

    useEffect(() => {
        if (featurePermissionsLoading || !csvMessageSenderReportPermission) return;
        if (csvId) {
            fetchCampaignReport();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [csvId, currentPage, pageSize, filters, featurePermissionsLoading, csvMessageSenderReportPermission]);

    const fetchCampaignReport = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/csv/msg-report`,
                {
                    params: {
                        csv_id: csvId,
                        page: currentPage,
                        limit: pageSize,
                        ...filters,
                    },
                    headers: getApiHeaders()
                }
            );

            setCampaignData(response.data);

            // Update pagination info from response
            if (response.data.pagination) {
                setTotalPages(response.data.pagination.totalPages || 0);
                setTotalRecords(response.data.pagination.totalRecords || response.data.count || 0);
            } else {
                // Fallback if pagination not provided
                setTotalRecords(response.data.count || 0);
                setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch campaign report';
            setError(errorMessage);
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (newPage) => {
        setCurrentPage(newPage);
    };

    const handlePageSizeChange = (newPageSize) => {
        setPageSize(newPageSize);
        setCurrentPage(1);
    };

    const handleFiltersChange = (newFilters) => {
        setFilters(newFilters);
        setCurrentPage(1);
    };

    // ── Auto-refresh ─────────────────────────────────────────────────────────
    useEffect(() => {
        if (featurePermissionsLoading || !csvMessageSenderReportPermission) return;
        if (!autoRefreshInterval) return;
        const intervalId = setInterval(() => fetchCampaignReport(), autoRefreshInterval);
        return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoRefreshInterval, currentPage, filters, featurePermissionsLoading, csvMessageSenderReportPermission]);

    // ── Fetch channel/status options for filter dropdowns ────────────────────
    const fetchFilterOptions = async () => {
        try {
            const [chRes, stRes] = await Promise.all([
                fetchWrapper.get(`${process.env.REACT_APP_API_URL}/channel/list`),
                fetchWrapper.get(`${process.env.REACT_APP_API_URL}/status/list`),
            ]);
            setChannelOptions((chRes.data || []).map((c) => ({ label: getChannel(c.id)?.label || getChannelLabel(c.name), value: c.id })));
            setStatusOptions((stRes.data || []).map((s) => ({ label: s.name, value: s.id })));
            setStatusList(stRes.data || []);
        } catch (err) {
            console.error("Error loading filter options:", err);
        }
    };

    useEffect(() => {
        if (featurePermissionsLoading || !csvMessageSenderReportPermission) return;
        fetchFilterOptions();
    }, [featurePermissionsLoading, csvMessageSenderReportPermission]);

    // ── Download CSV ─────────────────────────────────────────────────────────
    const downloadCSV = async () => {
        try {
            setIsDownloading(true);
            const params = new URLSearchParams();
            params.append("customer_id", customer_id || "");
            if (filters.channel)   params.append("channel",    filters.channel);
            if (filters.status)    params.append("status",     filters.status);
            if (filters.startDate) params.append("start_date", filters.startDate);
            if (filters.endDate)   params.append("end_date",   filters.endDate);
            if (filters.search)    params.append("search",     filters.search);

            // Progress/failure now shows in the persistent top banner (survives a
            // reload) instead of a toast — see CsvExportProvider.
            await startExport({
                exportUrl: `${process.env.REACT_APP_API_URL}/msg-log/export-data?${params.toString()}`,
                label: "CSV Reports export",
                filenameFallback: `messages_${moment().format("YYYY-MM-DD_HH-mm-ss")}.csv`,
            });
        } finally {
            setIsDownloading(false);
        }
    };

    // ── Original helpers (unchanged) ─────────────────────────────────────────
    const calculateStats = () => {
        if (!campaignData) return null;

        const totalMessages = campaignData.count || 0;
        const sentCount = campaignData.msgSentCount || 0;
        const failedCount = campaignData.msgFail || 0;

        // Count statuses from data array
        const statusCounts = {
            sent: 0,
            delivered: 0,
            read: 0,
            failed: failedCount,
        };

        campaignData.data?.forEach((msg) => {
            const statusId = msg.status_id;
            // Based on your data, status_id 100 means "Message Sent"
            if (statusId === 100) {
                statusCounts.sent++;
            }
            // Add more status mappings as needed
            // Example: if (statusId === 101) statusCounts.delivered++;
        });

        const successRate = totalMessages > 0 ? ((statusCounts.sent / totalMessages) * 100).toFixed(2) : 0;
        const deliveryRate = totalMessages > 0 ? (((totalMessages - failedCount) / totalMessages) * 100).toFixed(2) : 0;

        return {
            totalMessages,
            sentCount: statusCounts.sent,
            failedCount,
            successRate,
            deliveryRate,
            statusCounts,
        };
    };

    const getChannelName = (channelId) => getChannel(channelId)?.label || 'Unknown';

    const handleClick = () => {
        navigate(-1);
    };

    if (featurePermissionsLoading) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Spinner size="xl" color="blue.500" thickness="4px" />
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    if (!csvMessageSenderReportPermission) {
        return (
            <>
                <Header />
                <BoxWrapper>
                    <Center h="400px">
                        <Box textAlign="center">
                            <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                                No Access
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                                You don't have permission to view this sections.
                            </Text>
                        </Box>
                    </Center>
                </BoxWrapper>
            </>
        );
    }

    if (loading && !campaignData) {
        return (
            <Center h="100vh">
                <VStack spacing={4}>
                    <Spinner size="xl" color="blue.500" thickness="4px" />
                    <Text color="gray.600">Loading campaign report...</Text>
                </VStack>
            </Center>
        );
    }

    if (error) {
        return (
            <Center h="100vh">
                <VStack spacing={4}>
                    <FiAlertCircle size={48} color="#E53E3E" />
                    <Text color="red.600" fontSize="lg">Failed to load campaign report</Text>
                    <Text color="gray.600" fontSize="sm">{error}</Text>
                    <Button colorScheme="blue" onClick={fetchCampaignReport}>
                        Retry
                    </Button>
                </VStack>
            </Center>
        );
    }

    // if (!campaignData || !campaignData.data || campaignData.data.length === 0) {
    //     return (
    //         <Center h="100vh">
    //             <VStack spacing={4}>
    //                 <FiAlertCircle size={48} color="#A0AEC0" />
    //                 <Text color="gray.600" fontSize="lg">No campaign data found</Text>
    //                 <Button colorScheme="blue" onClick={fetchCampaignReport}>
    //                     Refresh
    //                 </Button>
    //             </VStack>
    //         </Center>
    //     );
    // }

    const stats = calculateStats();
    const firstMessage = campaignData?.data?.[0];
    const channelName = firstMessage ? getChannelName(firstMessage.msg_channel_id) : 'Unknown';

    return (
        <>
            <Header />
            <BoxWrapper>
                {/* Header */}
                <Box mb={6}>
                    <Button
                        leftIcon={<FiArrowLeft />}
                        variant="ghost"
                        mb={4}
                        ml={-2}
                        onClick={handleClick}
                    >
                        Back to Campaigns
                    </Button>

                    <Flex justify="space-between" align="flex-start">
                        <Box>
                            <HStack spacing={2} mb={2}>
                                <FiFileText size={28} color="var(--brand-primary)" />
                                <Text fontSize="2xl" fontWeight={"bold"}>Campaign Report</Text>
                            </HStack>

                            <HStack spacing={3} fontSize="sm" color="gray.600">
                                <HStack spacing={1}>
                                    <FiCalendar size={16} />
                                    <Text>{firstMessage ? new Date(firstMessage.receive_date).toLocaleString() : 'N/A'}</Text>
                                </HStack>
                                <Text>•</Text>
                                <Text fontFamily="mono" fontSize="xs">{csvId}</Text>
                                <Text>•</Text>
                                <Tag colorScheme="green" px={2} py={1}>Completed</Tag>
                            </HStack>
                        </Box>

                        {/* <HStack spacing={2}>
                            <Button
                                leftIcon={<FiDownload />}
                                variant="outline"
                                onClick={() => downloadReport('csv')}
                            >
                                Export CSV
                            </Button>
                            <Button
                                leftIcon={<FiDownload />}
                                variant="outline"
                                onClick={() => downloadReport('excel')}
                            >
                                Export Excel
                            </Button>
                        </HStack> */}
                    </Flex>
                </Box>

                {/* Campaign Overview Cards */}
                <Flex gap={4} mb={6} wrap="nowrap">

                    {/* Total Messages */}
                    <Box flex="1" className="box-style-card" p={5} borderRadius="lg" boxShadow="sm" bg="white">
                        <Flex justify="space-between" align="center" mb={3}>
                            <Text color="gray.600" fontSize="sm">
                                Total Messages
                            </Text>
                            <Box w={10} h={10} bg="blue.50" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                                <FiUsers size={20} color="var(--brand-primary-hover)" />
                            </Box>
                        </Flex>
                        <Text fontSize="3xl" fontWeight="semibold">
                            {formatNumber(campaignData?.count)}
                        </Text>
                    </Box>

                    {/* Messages Sent */}
                    <Box flex="1" className="box-style-card" p={5} borderRadius="lg" boxShadow="sm" bg="white">
                        <Flex justify="space-between" align="center" mb={3}>
                            <Text color="gray.600" fontSize="sm">
                                Messages Sent
                            </Text>
                            <Box w={10} h={10} bg="green.50" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                                <FiCheckCircle size={20} color="#2F855A" />
                            </Box>
                        </Flex>
                        <Text fontSize="3xl" fontWeight="semibold" color="green.600">
                            {formatNumber(campaignData?.msgSentCount)}
                        </Text>
                        {/* <Text fontSize="xs" color="gray.600" mt={1}>
                            Success Rate: {stats.successRate}%
                        </Text> */}
                    </Box>

{/* Pending Delivery */}
                    <Box flex="1" className="box-style-card" p={5} borderRadius="lg" boxShadow="sm" bg="white">
                        <Flex justify="space-between" align="center" mb={3}>
                            <Text color="gray.600" fontSize="sm">
                                Pending Delivery
                            </Text>
                            <Box w={10} h={10} bg="orange.50" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                                <FiClock size={20} color="#C05621" />
                            </Box>
                        </Flex>
                        <Text fontSize="3xl" fontWeight="semibold" color="orange.500">
                            {formatNumber(campaignData?.pendingDelivery)}
                        </Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            Pending Messages
                        </Text>
                    </Box>
                    {/* Delivered */}
                    <Box flex="1" className="box-style-card" p={5} borderRadius="lg" boxShadow="sm" bg="white">
                        <Flex justify="space-between" align="center" mb={3}>
                            <Text color="gray.600" fontSize="sm">
                                Delivered
                            </Text>
                            <Box w={10} h={10} bg="purple.50" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                                <FiMessageSquare size={20} color="#6B46C1" />
                            </Box>
                        </Flex>
                        <Text fontSize="3xl" fontWeight="semibold" color="purple.600">
                            {formatNumber(campaignData?.deliveredCount)}
                        </Text>
                        {/* <Text fontSize="xs" color="gray.600" mt={1}>
                            Delivery Rate: {stats.deliveryRate}%
                        </Text> */}
                    </Box>

                    {/* Failed */}
                    <Box flex="1" className="box-style-card" p={5} borderRadius="lg" boxShadow="sm" bg="white">
                        <Flex justify="space-between" align="center" mb={3}>
                            <Text color="gray.600" fontSize="sm">
                                Failed
                            </Text>
                            <Box w={10} h={10} bg="red.50" borderRadius="lg" display="flex" alignItems="center" justifyContent="center">
                                <FiAlertCircle size={20} color="#C53030" />
                            </Box>
                        </Flex>
                        <Text fontSize="3xl" fontWeight="semibold" color="red.600">
                            {formatNumber(campaignData?.msgFail)}
                        </Text>
                        <Text fontSize="xs" color="gray.600" mt={1}>
                            Failed Messages
                        </Text>
                    </Box>

                    

                </Flex>

                <CSVCampaignReportWithTable
                    // Data directly from csv/msg-report response
                    list={campaignData?.data || []}
                    totalItems={campaignData?.count || 0}
                    loading={loading}
                    // Pagination — reuse existing state
                    activePage={currentPage}
                    onPageChange={handlePageChange}
                    // Filters — reuse existing handler
                    onFiltersChange={handleFiltersChange}
                    // Refresh — reuse existing fetch
                    onRefresh={fetchCampaignReport}
                    // Dropdown options for filter panel
                    channelOptions={channelOptions}
                    statusOptions={statusOptions}
                    statusList={statusList}
                    // Export
                    isDownloading={isDownloading}
                    isExporting={isExporting}
                    onDownload={downloadCSV}
                    // Auto-refresh
                    autoRefreshInterval={autoRefreshInterval}
                    onAutoRefreshChange={setAutoRefreshInterval}
                />
            </BoxWrapper>
        </>
    );
}